import React, { useState, useEffect, useMemo } from 'react';
import { Container, Spinner, Alert, Button, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import LeagueStandingsTable from './LeagueStandingsTable';
import { getLevelModifierClass } from '../utils/levelColors';

const API_URL = process.env.REACT_APP_API_URL;

const LeagueDetails = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState("");
  const [availableYears, setAvailableYears] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [standings, setStandings] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);

  useEffect(() => {
    const fetchSeasons = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_URL}/seasons`);
        const allSeasons = res.data || [];
        setSeasons(allSeasons);

        const years = [
          ...new Set(
            allSeasons
              .map((season) =>
                season.date_start ? new Date(season.date_start).getFullYear() : null
              )
              .filter((year) => year !== null)
          ),
        ].sort((a, b) => b - a);

        setAvailableYears(years);
        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } catch (err) {
        setError("Không thể tải danh sách mùa giải.");
      } finally {
        setLoading(false);
      }
    };
    fetchSeasons();
  }, []);

  useEffect(() => {
    if (!selectedYear || seasons.length === 0) return;

    const fetchYearData = async () => {
      try {
        setLoading(true);
        setError(null);

        const seasonForYear = seasons.find(
          s => new Date(s.date_start).getFullYear().toString() === selectedYear
        );

        if (!seasonForYear) {
          setStandings([]);
          setSelectedLevelId(null);
          return;
        }

        const res = await axios.get(
          `${API_URL}/public/league/standings?season_id=${seasonForYear.id}`
        );
        const standingsData = res.data || [];
        setStandings(standingsData);

        setSelectedLevelId((currentLevelId) => {
          if (standingsData.length === 0) return null;

          const currentStillExists = standingsData.some(
            (level) => String(level.level_id) === String(currentLevelId)
          );

          return currentStillExists ? currentLevelId : standingsData[0].level_id;
        });
      } catch (err) {
        console.error(`Error fetching data for year ${selectedYear}:`, err);
        setError(`Không thể tải dữ liệu cho năm ${selectedYear}.`);
        setStandings([]);
        setSelectedLevelId(null);
      } finally {
        setLoading(false);
      }
    };

    fetchYearData();
  }, [selectedYear, seasons]);

  const selectedYearIndex = availableYears.indexOf(Number(selectedYear));

  const handlePrevYear = () => {
    if (selectedYearIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[selectedYearIndex + 1].toString());
    }
  };

  const handleNextYear = () => {
    if (selectedYearIndex > 0) {
      setSelectedYear(availableYears[selectedYearIndex - 1].toString());
    }
  };

  const allLevels = useMemo(() => standings || [], [standings]);

  const currentLevelData = useMemo(() => {
    if (!selectedLevelId) return null;
    return allLevels.find(l => String(l.level_id) === String(selectedLevelId));
  }, [allLevels, selectedLevelId]);

  const selectedSeason = useMemo(() => {
    if (!selectedYear) return null;

    return seasons.find(
      season => new Date(season.date_start).getFullYear().toString() === selectedYear
    );
  }, [seasons, selectedYear]);

  const tableCount = useMemo(
    () => allLevels.reduce((total, level) => total + (level.tables?.length || 0), 0),
    [allLevels]
  );

  const teamEntryCount = useMemo(
    () => allLevels.reduce(
      (levelTotal, level) => levelTotal + (level.tables || []).reduce(
        (tableTotal, table) => tableTotal + (table.teams?.length || 0),
        0
      ),
      0
    ),
    [allLevels]
  );

  const seasonName = selectedSeason?.name || selectedSeason?.season_name || 'Chưa chọn mùa giải';

  if (loading && standings.length === 0) {
    return (
      <Container className="league-page py-4">
        <div className="league-loading-state">
          <Spinner animation="border" />
          <p>Đang tải dữ liệu giải đấu...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="league-page py-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  const renderContent = () => {
    if (!currentLevelData) return null;

    const tables = currentLevelData.tables || [];

    if (tables.length === 0) {
      return (
        <Alert variant="info" className="league-empty-state">
          Chưa có dữ liệu bảng đấu cho hạng này.
        </Alert>
      );
    }

    return (
      <div className="league-table-grid">
        {tables.map(table => (
          <section key={table.table_id}>
            <LeagueStandingsTable
              title={table.table_name}
              teams={table.teams}
            />
          </section>
        ))}
      </div>
    );
  };

  return (
    <Container className="league-page py-4">
      <header className="league-hero">
        <div className="league-hero-copy">
          <p className="client-kicker">HDSPL standings</p>
          <h1>Bảng Xếp Hạng</h1>
          <p>
            Theo dõi thứ tự các đội theo hạng đấu, bảng đấu và điểm số cập nhật trong mùa.
          </p>
          <div className="league-stat-strip" aria-label="Tổng quan bảng xếp hạng">
            <span>
              <strong>{selectedYear || '-'}</strong>
              Mùa giải
            </span>
            <span>
              <strong>{allLevels.length}</strong>
              Hạng đấu
            </span>
            <span>
              <strong>{tableCount}</strong>
              Bảng
            </span>
            <span>
              <strong>{teamEntryCount}</strong>
              Lượt đội
            </span>
          </div>
        </div>

        <div className="league-season-panel">
          <span>Mùa đang xem</span>
          {availableYears.length > 0 && (
            <div className="league-year-control">
              <Button
                variant="link"
                aria-label="Năm trước"
                onClick={handlePrevYear}
                disabled={selectedYearIndex === availableYears.length - 1}
              >
                ‹
              </Button>
              <strong>{selectedYear}</strong>
              <Button
                variant="link"
                aria-label="Năm sau"
                onClick={handleNextYear}
                disabled={selectedYearIndex === 0}
              >
                ›
              </Button>
            </div>
          )}
          <small>{seasonName}</small>
          <Button
            variant="link"
            href="/match-results"
            className="league-schedule-link"
          >
            Xem lịch thi đấu
          </Button>
        </div>
      </header>

      {selectedLevelId && allLevels.length > 0 && (
        <Tabs
          activeKey={String(selectedLevelId)}
          onSelect={(k) => setSelectedLevelId(k)}
          className="league-level-tabs"
          fill
        >
          {allLevels.map(level => (
            <Tab
              key={level.level_id}
              eventKey={String(level.level_id)}
              title={level.level_name}
              tabClassName={getLevelModifierClass(level.level_name)}
            >
              {renderContent()}
            </Tab>
          ))}
        </Tabs>
      )}

      {standings.length === 0 && !loading && (
        <Alert variant="info" className="league-empty-state">
          Không có dữ liệu bảng xếp hạng cho năm {selectedYear}.
        </Alert>
      )}
    </Container>
  );
};

export default LeagueDetails;
